import { useEffect, useMemo, useState } from "react";
import { Upload, FileSpreadsheet, Loader2, Link2, Plus, Check, Pencil, Trash2, Calendar, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";
import { getSavedSheets, createSavedSheet, updateSavedSheet, deleteSavedSheet } from "../lib/api";
import { toast } from "sonner";

const HEB_MONTHS = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

const HEB_DAYS = ["יום ראשון", "יום שני", "יום שלישי", "יום רביעי", "יום חמישי", "יום שישי", "שבת"];

// Compute the effective send day for a given month: last day of month, shifted back if Saturday.
function computeEffectiveDay(year, month /* 1-12 */) {
  const lastDay = new Date(year, month, 0).getDate();
  let day = lastDay;
  while (new Date(year, month - 1, day).getDay() === 6 && day > 1) {
    day -= 1;
  }
  return day;
}

const EMPTY_FORM = () => ({
  id: null,
  name: "",
  url: "",
  year: new Date().getFullYear(),
  time: "10:00",
  active: true,
});

export const ImportSource = ({ onFileUpload, onGSheetImport, loading }) => {
  const [dragging, setDragging] = useState(false);
  const [savedSheets, setSavedSheets] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const isEditing = !!form.id;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const yearOptions = [currentYear, currentYear + 1, currentYear + 2];

  // Build the preview list: for the selected year, show one row per month.
  // For current year - skip past months.
  const previewRows = useMemo(() => {
    const startMonth = form.year === currentYear ? currentMonth : 1;
    const rows = [];
    for (let m = startMonth; m <= 12; m++) {
      const day = computeEffectiveDay(form.year, m);
      const dow = new Date(form.year, m - 1, day).getDay();
      const lastDay = new Date(form.year, m, 0).getDate();
      const wasShifted = dow !== 6 && day < lastDay && new Date(form.year, m - 1, lastDay).getDay() === 6;
      rows.push({
        month: m,
        monthName: HEB_MONTHS[m - 1],
        day,
        dowName: HEB_DAYS[dow],
        wasShifted,
      });
    }
    return rows;
  }, [form.year, currentYear, currentMonth]);

  const loadSheets = async () => {
    try {
      const data = await getSavedSheets();
      setSavedSheets(data.sheets || []);
    } catch {
      setSavedSheets([]);
    }
  };

  useEffect(() => {
    loadSheets();
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (!/\.(csv|xlsx|xls)$/i.test(file.name)) {
      alert("סוג קובץ לא נתמך. יש להעלות CSV או Excel");
      return;
    }
    onFileUpload(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const openAdd = () => { setForm(EMPTY_FORM()); setShowDialog(true); };
  const openEdit = (sheet) => {
    setForm({
      id: sheet.id,
      name: sheet.name || "",
      url: sheet.url || "",
      year: currentYear,
      time: sheet.schedule_time || "10:00",
      active: sheet.active !== false,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error("יש למלא שם וקישור");
      return;
    }
    // schedule_day: null = last day of month (auto). The backend computes per-month with Saturday shift.
    const payload = {
      name: form.name.trim(),
      url: form.url.trim(),
      schedule_day: null, // means: auto last non-Saturday day of every month
      schedule_time: form.time || "10:00",
      active: form.active,
    };
    try {
      if (isEditing) {
        await updateSavedSheet(form.id, payload);
        toast.success("התזמון עודכן בהצלחה");
      } else {
        await createSavedSheet(payload);
        toast.success("הגיליון נשמר ומתוזמן אוטומטית");
      }
      setShowDialog(false);
      await loadSheets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשמירה");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSavedSheet(id);
      toast.success("הגיליון נמחק");
      setConfirmDelete(null);
      await loadSheets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה במחיקה");
    }
  };

  return (
    <>
      <Tabs defaultValue="gsheet" className="bg-white rounded-2xl border border-border" data-testid="import-tabs">
        <TabsList className="w-full justify-start bg-stone-50 p-1 rounded-t-2xl rounded-b-none h-auto">
          <TabsTrigger value="gsheet" className="data-[state=active]:bg-white gap-2" data-testid="tab-gsheet">
            <Link2 className="h-4 w-4" />
            Google Sheets
          </TabsTrigger>
          <TabsTrigger value="file" className="data-[state=active]:bg-white gap-2" data-testid="tab-file">
            <FileSpreadsheet className="h-4 w-4" />
            העלאת CSV / Excel
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gsheet" className="p-6 sm:p-8 mt-0">
          {savedSheets.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-brand-50 flex items-center justify-center mb-4">
                <Link2 className="h-8 w-8 text-brand" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">חיבור לגיליון Google Sheets</h3>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                חברי את הגיליון פעם אחת - המערכת תזכור אותו לעתיד
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 text-right mb-4">
                <p className="font-semibold mb-1">⚠️ לפני שמחברים - חובה לשתף את הגיליון:</p>
                <ol className="list-decimal pr-4 space-y-0.5">
                  <li>בגיליון לחצי "שתף" (פינה ימנית-עליונה)</li>
                  <li>ב"גישה כללית" - שני ל"כל מי שיש לו הקישור"</li>
                  <li>וודאי שבחרת "צופה"</li>
                </ol>
              </div>
              <Button onClick={openAdd} className="bg-brand hover:bg-brand-hover text-white" data-testid="add-first-sheet-button">
                <Plus className="h-4 w-4 ml-2" />
                חבר גיליון ראשון
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold">הגיליונות השמורים שלך</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    לחצי על גיליון כדי לטעון ממנו לקוחות
                  </p>
                </div>
                <Button onClick={openAdd} variant="outline" size="sm" data-testid="add-sheet-button">
                  <Plus className="h-4 w-4 ml-2" />
                  הוסף גיליון חדש
                </Button>
              </div>

              <div className="grid gap-3">
                {savedSheets.map((sheet) => (
                  <div
                    key={sheet.id}
                    className="flex items-center justify-between flex-wrap gap-3 p-4 border border-border rounded-xl hover:border-brand/40 hover:bg-brand-50/30 transition-colors group"
                    data-testid={`saved-sheet-${sheet.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-stone-900">{sheet.name}</p>
                        {sheet.active === false && (
                          <span className="text-[10px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full font-bold">
                            אוטומציה כבויה
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-stone-500 mt-1 flex-wrap">
                        <span dir="ltr" className="font-mono truncate max-w-[200px]">
                          {(sheet.url || "").replace(/^https?:\/\//, "")}
                        </span>
                        {sheet.active !== false && (
                          <span className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full">
                            ⏰ אוטומטי - סוף כל חודש בשעה {sheet.schedule_time || "10:00"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button"
                        onClick={() => setConfirmDelete(sheet)}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input bg-white hover:bg-red-50 hover:border-red-300 text-stone-600 hover:text-red-600 transition-colors"
                        title="מחיקת גיליון"
                        data-testid={`delete-sheet-${sheet.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button type="button"
                        onClick={() => openEdit(sheet)}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input bg-white hover:bg-stone-100 text-stone-600 hover:text-brand transition-colors"
                        title="עריכת גיליון"
                        data-testid={`edit-sheet-${sheet.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <Button
                        onClick={() => onGSheetImport(sheet.url)}
                        disabled={loading}
                        size="sm"
                        className="bg-brand hover:bg-brand-hover text-white"
                        data-testid={`load-sheet-${sheet.id}`}
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>טען עכשיו</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="file" className="mt-0">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={cn(
              "m-4 sm:m-6 rounded-xl border-2 border-dashed transition-all duration-200",
              dragging ? "border-brand bg-brand-50" : "border-stone-300 bg-white",
              "p-8 sm:p-10 text-center"
            )}
            data-testid="upload-zone"
          >
            <div className="mx-auto h-16 w-16 rounded-full bg-brand-50 flex items-center justify-center mb-4">
              <FileSpreadsheet className="h-8 w-8 text-brand" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">העלאת קובץ לקוחות</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
              גרור לכאן קובץ CSV / Excel עם רשימת הלקוחות
              <br />
              <span className="text-xs text-stone-500">
                עמודות: שם, טלפון, מחיר, תאריך פירעון (אופציונלי: תיאור, אימייל, ת.ז/ח.פ)
              </span>
            </p>
            <label htmlFor="file-input">
              <Button asChild disabled={loading} className="bg-brand hover:bg-brand-hover text-white cursor-pointer" data-testid="upload-file-button">
                <span>
                  {loading ? (
                    <><Loader2 className="h-4 w-4 ml-2 animate-spin" />מעבד קובץ...</>
                  ) : (
                    <><Upload className="h-4 w-4 ml-2" />בחר קובץ מהמחשב</>
                  )}
                </span>
              </Button>
            </label>
            <input
              id="file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Add / Edit dialog with year preview */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent dir="rtl" className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="sheet-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">
              {isEditing ? "עריכת גיליון" : "הוספת גיליון חדש"}
            </DialogTitle>
            <DialogDescription className="text-right">
              {isEditing
                ? "עדכני את פרטי הגיליון והתזמון השנתי"
                : "המערכת תשלח את הגיליון אוטומטית בסוף כל חודש - לא בשבת"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>שם הגיליון</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="למשל: לקוחות חודשיים"
                className="mt-1"
                data-testid="sheet-name-input"
              />
            </div>
            <div>
              <Label>קישור Google Sheets</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                dir="ltr"
                className="mt-1 text-right"
                data-testid="sheet-url-input"
              />
            </div>

            {/* Yearly schedule preview */}
            <div className="bg-brand-50/40 border-2 border-brand/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-brand" />
                  <Label className="font-bold text-brand-700 text-sm">תזמון אוטומטי שנתי</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="active-toggle" className="text-xs cursor-pointer font-semibold">
                    {form.active ? "🟢 פעיל" : "⏸ כבוי"}
                  </Label>
                  <Switch
                    id="active-toggle"
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="sheet-active-toggle"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">בחרי שנה</Label>
                  <select
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: parseInt(e.target.value, 10) })}
                    className="w-full h-10 mt-1 rounded-md border border-input bg-white px-3 text-sm focus:ring-2 focus:ring-brand focus:outline-none font-bold"
                    data-testid="sheet-year-select"
                    disabled={!form.active}
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">שעת שליחה</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                    className="mt-1"
                    data-testid="sheet-time-input"
                    disabled={!form.active}
                  />
                </div>
              </div>

              {form.active && (
                <>
                  <div className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="schedule-preview-table">
                    <div className="bg-stone-50 px-3 py-2 grid grid-cols-3 gap-2 text-[11px] font-bold text-stone-600 border-b border-stone-200">
                      <span>חודש</span>
                      <span className="text-center">יום</span>
                      <span className="text-left">יום בשבוע</span>
                    </div>
                    <div className="max-h-[280px] overflow-y-auto">
                      {previewRows.map((row) => (
                        <div
                          key={row.month}
                          className="px-3 py-2 grid grid-cols-3 gap-2 text-sm border-b border-stone-100 last:border-b-0 hover:bg-brand-50/30"
                          data-testid={`schedule-row-${row.month}`}
                        >
                          <span className="font-semibold text-stone-800">{row.monthName}</span>
                          <span className="text-center font-mono font-bold tabular-nums text-brand">{row.day}</span>
                          <span className="text-left text-xs text-stone-600 flex items-center gap-1 justify-end">
                            {row.dowName}
                            {row.wasShifted && (
                              <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">
                                🕯️ הוזז משבת
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-stone-700 leading-relaxed">
                    💡 <strong>שמירת שבת:</strong> אם היום האחרון של החודש נופל בשבת,
                    המערכת מזיזה אוטומטית את השליחה ליום שישי באותה שעה.
                  </p>
                </>
              )}

              {!form.active && (
                <p className="text-[11px] text-stone-700 leading-relaxed bg-white border border-amber-200 rounded p-2">
                  ⏸ <strong>אוטומציה כבויה</strong> - הגיליון יישלח רק כשתלחצי "טען עכשיו" ידנית.
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="flex-row-reverse gap-2 sm:gap-2">
            <Button
              onClick={handleSave}
              className="bg-whatsapp hover:bg-whatsapp-hover text-white font-semibold"
              data-testid="save-sheet-button"
            >
              <Send className="h-4 w-4 ml-2" />
              {form.active ? "שלח אוטומטי" : "שמור גיליון"}
            </Button>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-save-sheet-button">
              ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent dir="rtl" className="sm:max-w-sm" data-testid="delete-sheet-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">מחיקת גיליון</DialogTitle>
            <DialogDescription className="text-right">
              האם למחוק את הגיליון <strong>{confirmDelete?.name}</strong>? הפעולה אינה הפיכה.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row-reverse gap-2 sm:gap-2">
            <Button
              onClick={() => handleDelete(confirmDelete.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-sheet"
            >
              <Trash2 className="h-4 w-4 ml-2" />
              כן, מחק
            </Button>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
